import { useCallback, useContext, useEffect, useState } from 'react'
import { Form } from '../../../components/Form'
import {
  Box,
  CircularProgress,
  Container,
  Typography,
  Alert,
} from '@mui/material'
import { BackButton } from '../../../components/navigation/BackButton'
import { useGetEntityFormByType } from '../../../hooks/entityFormHook'
import { useNavigate, useParams } from 'react-router-dom'
import { Fetching } from '../../../components/Fetching'
import { useGetTimeSheetsByDate } from '../timesheets/hooks'
import { getCurrentDate } from '../../../lib/time'
import { ListedTimeSheets } from '../../../components/timesheet/ListedTimesheets'
import { useGetDailyReportFormData, useGetProject } from '../hooks'
import dayjs from 'dayjs'
import { useGetReportNumberExists, useMutateCreateDailyReport } from './hooks'
import SnackBarContext from '../../../context/SnackBarContext'
import { debounce } from 'lodash'

export const DailyReportFormNew = (props: any) => {
  const defaultReportState = {
    project: '',
    projectName: '',
    projectNumber: '0000',
    date: getCurrentDate(),
    clientName: '',
    service: '',
    activityLocation: '',
    reportNumber: null,
    weather: 'zzz',
  }
  const requiredFields = [
    'projectName',
    'projectNumber',
    'clientName',
    'service',
    'activityLocation',
    'reportNumber',
    'weather',
  ]
  const { id } = useParams()
  const navigate = useNavigate()
  const [reportDate, setReportDate] = useState<any>(getCurrentDate())
  const [reportData, setReportData] = useState<any>(defaultReportState)
  const [reportErrors, setReportErrors] = useState<any>(null)
  const [reportNumberError, setReportNumberError] = useState<any>(null)
  const { data: form, isLoading: isFormLoading } = useGetEntityFormByType(
    'projectForm',
    'dailyreport'
  )
  const { data: timeCards, isLoading: isLoadingTC } = useGetTimeSheetsByDate(
    'timecards',
    id,
    { date: reportDate }
  )
  const { data: projectData, isLoading: isLoadingProject } = useGetProject(
    'project',
    id
  )

  const { data: formData, isLoading: isLoadingFormData } =
    useGetDailyReportFormData('projectReportFormData', id)
  const { mutateAsync: createDailyReport, isLoading: isLoadingMutation } =
    useMutateCreateDailyReport('dailyreports')

  const { toggleSnackbar } = useContext(SnackBarContext)
  const [reportNumber, setReportNumber] = useState<any | undefined>(null)
  const { data: reportNumberExists, isFetching: isFetchingReportNumber } =
    useGetReportNumberExists('reportNumber', reportNumber)
  const [isReportNumberExistsFetching, setIsReportNumberExistsFetching] =
    useState(false)

  const onChangeHandler = (data: any) => {
    if (data.hasOwnProperty('date')) {
      setReportDate(data.date)
    }
    if (data?.reportNumber) {
      const sanitizedReportNumber = String(
        data?.reportNumber?.toString()
      )?.replace(/\D/g, '')
      if (sanitizedReportNumber.length > 10) {
        data.reportNumber = sanitizedReportNumber.slice(0, 10)
      } else {
        data.reportNumber = sanitizedReportNumber
      }

      handleSearchingReportNumber(data.reportNumber)
    }
    setReportData(data)
  }

  const handleSearchingReportNumber = useCallback(
    debounce((number: string) => {
      number !== reportNumber && setIsReportNumberExistsFetching(true)
      setReportNumber(number)
    }, 500),
    []
  )

  const validateRequiredFields = (dailyReportData: any) => {
    const errors: string[] = []
    requiredFields.forEach(field => {
      if (!dailyReportData[field] || dailyReportData[field] === '') {
        errors.push(`${field} is required`)
      }
    })
    return errors
  }
  const projectOptions = projectData
    ? [{ value: projectData._id, label: projectData.projectName }]
    : []
  if (form && form.fields) {
    const projectField = form.fields.find(
      (field: any) => field.name === 'project'
    )
    if (projectField) {
      projectField.options = projectOptions
    }
  }
  const onSubmitHandler = useCallback(
    debounce(async report => {
      const formData = new FormData()
      const dailyReportData = {
        ...report,
        project: id,
        date: dayjs(reportDate).format('YYYY-MM-DD'),
        timesheets: timeCards,
        finalized: true,
        clientEmails: projectData.clientEmails,
        account: projectData.account,
      }
      const errors = validateRequiredFields(dailyReportData)
      if (errors.length > 0) {
        setReportErrors(errors)
        return
      }
      try {
        formData.append('dailyReport', JSON.stringify(dailyReportData))
        await createDailyReport({
          dailyReport: JSON.stringify(dailyReportData),
        })
        navigate(`/projects/${projectData._id}`)
        toggleSnackbar('Daily report added successfully', 'success')
      } catch (error) {
        console.error('Error creating report:', error)
        toggleSnackbar('Failed to create report.', 'error')
      }
    }, 500),
    [reportData]
  )

  useEffect(() => {
    if (reportNumberExists && reportNumberExists.exists) {
      setReportNumberError('Report number already exists')
    } else {
      setReportNumberError(null)
    }
    if (projectData && formData) {
      const updates = {
        project: projectData._id,
        projectName: projectData.projectName,
        projectNumber: projectData.projectNumber,
        clientName: projectData.clientName,
        service: projectData.service,
        activityLocation: projectData.locations[0]?.address,
        reportNumber: !reportNumber ? formData?.nextReportNumber : reportNumber,
        weather: formData.weather,
      }

      const errors = validateRequiredFields(updates)
      if (errors.length > 0) {
        setReportErrors(errors)
      }

      setReportData((prevReportData: any) => ({
        ...prevReportData,
        ...updates,
      }))
    }
  }, [formData, projectData])

  useEffect(() => {
    if (reportNumber) {
      if (reportNumberExists?.exists) {
        setReportNumber(parseInt(reportNumber) + 1)
      } else {
        setReportData({
          ...reportData,
          reportNumber: parseInt(reportNumber),
        })
      }
      setIsReportNumberExistsFetching(false)
    } else {
      setReportNumber(formData?.nextReportNumber)
    }
  }, [reportNumberExists, formData])

  if (
    isFormLoading ||
    isLoadingProject ||
    isLoadingTC ||
    isLoadingFormData ||
    isLoadingMutation
  ) {
    return <Fetching />
  }
  const nonEditableFields = [
    'projectNumber',
    'clientName',
    'activityLocation',
    'service',
  ]
  return (
    <>
      <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
        <Container maxWidth='lg'>
          <BackButton />
        </Container>
      </Box>
      {form ? (
        <>
          <Box sx={{ marginTop: 1, marginLeft: 2, marginRight: 2 }}>
            <Container maxWidth='lg'>
              <Typography variant='h4' gutterBottom>
                {form.formName} for - {projectData.projectName}:{' '}
                {reportData.reportNumber}
              </Typography>

              {isReportNumberExistsFetching && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0.5rem',
                  }}>
                  <CircularProgress size='1rem' />
                  <div style={{ marginLeft: '0.5rem' }}>
                    Verifying reportNumber...
                  </div>
                </div>
              )}
              {reportErrors && (
                <Alert severity='error' sx={{ marginBottom: 2 }}>
                  {reportErrors}
                </Alert>
              )}
              {reportData ? (
                <Form
                  fields={form.fields.map((field: any) => ({
                    ...field,
                    disabled: nonEditableFields.includes(field.name),
                  }))}
                  onSubmit={onSubmitHandler}
                  record={reportData}
                  onFormDataChange={onChangeHandler}
                  externalError={reportErrors || reportNumberError}
                  submitLabel='Finalize'>
                  {timeCards && (
                    <ListedTimeSheets
                      timeSheets={timeCards}
                      keyName='projectTimesheets'
                      id={id}
                    />
                  )}
                </Form>
              ) : null}
            </Container>
          </Box>
        </>
      ) : null}
    </>
  )
}
